import React, { useState, useEffect, useCallback } from "react";
import loader from "../../../../../components/recharts/loader.gif";
import { ChevronLeft, Edit, Plus, ChevronRight } from "lucide-react";
import { AddOfferDataModal } from "../../PowerProcurement/AddOffer";
import { EditOfferDataModal } from "../../PowerProcurement/EditOffer";
import {
  AddTraderRuvnlOffersData,
  fetchTraderRuvnlOffersData,
  UpdateTraderRuvnlOffersToggleData,
  getStatusTraderRuvnlOffersData,
  fetchOptimalTraderRuvnlOffersData,
} from "../../../Utility/RestApi";
import { useSelector } from "react-redux";

interface TraderOffer {
  start_slot: string;
  end_slot: string;
  quantum: string;
  rate: string;
}

interface OfferRow {
  start_date: string;
  end_date: string;
  type: "RTC" | "PEAK" | "CUSTOM";
  id: number;
  trader_offer: TraderOffer[];
  status?: boolean;
}

interface TimeSlot {
  id: string;
  start_slot: string;
  end_slot: string;
  quantum: string;
  rate: string;
  label?: string;
}

interface TransformedOffer {
  id: string;
  startDate: string;
  endDate: string;
  type: "RTC" | "PEAK" | "CUSTOM";
  timeSlots: TimeSlot[];
  originalId?: number;
  status?: boolean;
}

interface PPEFormData {
  startDate: string;
  endDate: string;
  scenario: string;
  offers: OfferRow[];
}

interface PPEConsoleProps {
  onApply: (data: PPEFormData) => void;
  onReset: () => void;
  loading: boolean;
  refreshKey: any;
  setRefreshKey: any;
  isSliderOpen: boolean;
  activeTab: string;
}

export function PPEConsole({
  onApply,
  onReset,
  refreshKey,
  setRefreshKey,
  loading: applyLoading,
  isSliderOpen,
  activeTab,
}: PPEConsoleProps) {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [offers, setOffers] = useState<OfferRow[]>([]);
  const [offersOptimal, setOptimalOffers] = useState<OfferRow[]>([]);
  const [editingOffer, setEditingOffer] = useState<TransformedOffer | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scenario, setScenario] = useState<string>("");
  const SelectedStartDate = useSelector(
    (state: any) => state.ProcurementOptimizerRuvnlDate.startDate
  );
  const SelectedEndDate = useSelector(
    (state: any) => state.ProcurementOptimizerRuvnlDate.endDate
  );
  // Fetch offers from backend
  const fetchOffers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchTraderRuvnlOffersData();
      const optimalData = await fetchOptimalTraderRuvnlOffersData();
      const statusPower = await getStatusTraderRuvnlOffersData();
      if (statusPower) {
        const mcp = statusPower?.mcpScenario;
        setScenario(mcp);
      }
      const offersWithStatus = data.map((offer: OfferRow) => ({
        ...offer,
        status: offer.status !== undefined ? offer.status : true,
      }));
      setOptimalOffers(optimalData);
      setOffers(offersWithStatus);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to fetch offers";
      setError(errorMessage);
      console.error("Error fetching offers:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load offers on mount
  useEffect(() => {
    fetchOffers();
  }, [fetchOffers, refreshKey, activeTab]);

  const handleAddOffers = () => {
    setIsAddModalOpen(true);
  };

  const transformOfferForEdit = (offer: OfferRow): TransformedOffer => {
    return {
      id: offer.id.toString(),
      startDate: offer.start_date,
      endDate: offer.end_date,
      type: offer.type,
      originalId: offer.id,
      status: offer.status,
      timeSlots: offer.trader_offer.map((traderOffer, index) => ({
        id: `slot-${offer.id}-${index}`,
        start_slot: traderOffer.start_slot,
        end_slot: traderOffer.end_slot,
        quantum: traderOffer.quantum,
        rate: traderOffer.rate,
        label:
          offer.type === "PEAK"
            ? index === 0
              ? "Morning Peak"
              : "Evening Peak"
            : undefined,
      })),
    };
  };

  const handleEditClick = (offer: OfferRow) => {
    const transformedOffer = transformOfferForEdit(offer);
    setEditingOffer(transformedOffer);
    setIsEditModalOpen(true);
  };

  const handleAddSubmit = async (data: TransformedOffer[]) => {
    setLoading(true);
    setError(null);
    try {
      const apiData = data.map((offerRow) => {
        return {
          type: offerRow.type,
          start_date: offerRow.startDate,
          end_date: offerRow.endDate,
          trader_offer: offerRow.timeSlots.map(
            ({ id, label, quantum, rate, ...slot }) => ({
              ...slot,
              quantum: quantum,
              rate: rate,
            })
          ),
        };
      });
      await AddTraderRuvnlOffersData(apiData);
      setRefreshKey((prev: number) => prev + 1);
      await fetchOffers();
      setIsAddModalOpen(false);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to add offers";
      setError(errorMessage);
      console.error("Error adding offers:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditSubmit = () => {
    setIsEditModalOpen(false);
    setEditingOffer(null);
    fetchOffers();
    setRefreshKey((prev: number) => prev + 1);
  };

  const handleToggleOffer = (id: number) => {
    setOffers((prev) =>
      prev.map((offer) =>
        offer.id === id ? { ...offer, status: !offer.status } : offer
      )
    );
  };

  const handleScenarioChange = (value: string) => {
    setScenario(value);
  };

  const handleReset = () => {
    setScenario("");
    fetchOffers();
    onReset();
  };

  const handleApply = () => {
    const formData: PPEFormData = {
      startDate: offers[0]?.start_date || "",
      endDate: offers[0]?.end_date || "",
      scenario: scenario,
      offers: offers,
    };
    onApply(formData);
  };

  const getSlotLabel = (index: number, type: string) => {
    if (type === "PEAK") {
      return index === 0 ? "Morning Peak" : "Evening Peak";
    }
    return `Period ${index + 1}`;
  };

  // Filter only active offers
  const activeOffers = offers.filter((offer) => offer.status);

  // Condensed View (when slider is closed)
  if (!isSliderOpen) {
    return (
      <>
        {/* MODALS */}
        <AddOfferDataModal
          isModalOpen={isAddModalOpen}
          setIsModalOpen={setIsAddModalOpen}
          modalTitle="Add Offer"
          onSubmit={handleAddSubmit}
          loading={loading}
          setLoading={setLoading}
        />

        {isEditModalOpen && editingOffer && (
          <EditOfferDataModal
            isModalOpen={isEditModalOpen}
            setIsModalOpen={setIsEditModalOpen}
            offerData={editingOffer}
            onUpdate={handleEditSubmit}
          />
        )}

        <div className="w-full h-full bg-white flex flex-col">
          {/* Header */}
          <div className="flex justify-between items-center pb-2 border-b border-[#DFEAF2] mb-2 px-1">
            <h1 className="text-lg font-medium text-[#34656d]">
              Procurement Console
            </h1>
            <button
              onClick={handleAddOffers}
              className="flex items-center px-4 py-2 gap-2 text-base rounded-xl bg-[#FFF] text-[#F1935C] font-medium border border-[#F1935C] hover:bg-[#FFF5ED] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus strokeWidth={3} className="w-5 h-5" />
            </button>
          </div>

          <div className="flex-1 overflow-hidden flex flex-col">
            {/* Market Scenario Section */}
            <div className="border border-[#DFEAF2] rounded-xl p-2 bg-[#FFF] mb-2 w-[100%]">
              <h3 className="text-base text-[#34656d] font-medium mb-2 pb-1 border-b border-[#E8EAED]">
                Market Scenario
              </h3>
              <div className="flex items-center justify-between gap-x-4 gap-y-3 mb-3">
                <div>
                  <label className="block text-base text-[#9AA0A6] mb-0.5">
                    Start Date
                  </label>
                  <div className="text-base text-[#34656D] font-medium">
                    {SelectedStartDate}
                  </div>
                </div>
                <div>
                  <label className="block text-base text-[#9AA0A6] mb-0.5">
                    End Date
                  </label>
                  <div className="text-base text-[#34656D] font-medium">
                    {SelectedEndDate}
                  </div>
                </div>
              </div>
              {/* Scenario Select */}
              <div className="relative">
                <select
                  value={scenario}
                  onChange={(e) => handleScenarioChange(e.target.value)}
                  className="w-full py-1 px-4 pr-10 border border-[#DFEAF2] rounded-lg text-xl text-[#6F6D7C] bg-white appearance-none outline-none cursor-pointer"
                >
                  <option value="">Select Scenario</option>
                 <option value="1">Last Year</option>
                  <option value="2">Last 2 Year Avg</option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-[0px] top-[-2px] flex items-center pr-[10px] text-[#9AA0A6]">
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>
            </div>
            {activeTab === "Optimal" ? (
              <>
                <div className="flex-1 mb-2 border border-[#DFEAF2] rounded-xl bg-[#FFF] flex flex-col overflow-hidden">
                  <div className="p-2 pb-2 mb-0 border-b border-[#DFEAF2]">
                    <h3 className="text-base text-[#34656D] font-medium">
                      Optimal Offers
                    </h3>
                  </div>

                  {/* Scrollable Offers List - Shows ALL Active Offers */}
                  <div className="flex-1 overflow-y-auto px-2 custom-scroll">
                    <div className="space-y-3 py-2">
                      {loading && offers.length === 0 ? (
                        <div className="flex items-center justify-center py-8">
                          <img src={loader} width={100} alt="loader" />
                        </div>
                      ) : error ? (
                        <div className="text-center py-4 text-red-500 text-xs">
                          {error}
                        </div>
                      ) : offersOptimal.length === 0 ? (
                        <div className="text-center py-8 text-gray-400 text-xs">
                          No active offers
                        </div>
                      ) : (
                        offersOptimal.map((offer) => (
                          <div
                            key={offer.id}
                            className="border-b border-[#DFEAF2] pb-2 last:border-b-0"
                          >
                            <div className="flex items-center justify-between mb-1">
                              <div>
                                <div className="text-sm text-[#8C8A96]">
                                  Offer Name
                                </div>
                                <div
                                  className={`text-sm font-medium ${
                                    offer.type === "RTC"
                                      ? "text-[#F1935C]"
                                      : offer.type === "PEAK"
                                      ? "text-[#5B8A9F]"
                                      : "text-[#8B7DA8]"
                                  }`}
                                >
                                  {offer.type}
                                </div>
                              </div>
                              <div>
                                <div className="text-sm text-[#8C8A96]">
                                  Rate
                                </div>
                                <div className="text-sm text-[#34656D] font-medium">
                                  {offer.trader_offer[0]?.rate} ₹/kWh
                                </div>
                              </div>
                              <div>
                                <div className="text-sm text-[#8C8A96]">
                                  Quantum
                                </div>
                                <div className="text-sm text-[#34656D] font-medium">
                                  {offer.trader_offer[0]?.quantum} MW
                                </div>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="flex-1 mb-2 border border-[#DFEAF2] rounded-xl bg-[#FFF] flex flex-col overflow-hidden">
                  <div className="p-2 pb-2 mb-0 border-b border-[#DFEAF2]">
                    <h3 className="text-base text-[#34656D] font-medium">
                      Active Offers
                    </h3>
                  </div>

                  {/* Scrollable Offers List - Shows ALL Active Offers */}
                  <div className="flex-1 overflow-y-auto px-2 custom-scroll">
                    <div className="space-y-3 py-2">
                      {loading && offers.length === 0 ? (
                        <div className="flex items-center justify-center py-8">
                          <img src={loader} width={100} alt="loader" />
                        </div>
                      ) : error ? (
                        <div className="text-center py-4 text-red-500 text-xs">
                          {error}
                        </div>
                      ) : activeOffers.length === 0 ? (
                        <div className="text-center py-8 text-gray-400 text-xs">
                          No active offers
                        </div>
                      ) : (
                        activeOffers.map((offer) => (
                          <div
                            key={offer.id}
                            className="border-b border-[#DFEAF2] pb-2 last:border-b-0"
                          >
                            <div className="flex items-center justify-between mb-1">
                              <div>
                                <div className="text-sm text-[#8C8A96]">
                                  Offer Name
                                </div>
                                <div
                                  className={`text-sm font-medium ${
                                    offer.type === "RTC"
                                      ? "text-[#F1935C]"
                                      : offer.type === "PEAK"
                                      ? "text-[#5B8A9F]"
                                      : "text-[#8B7DA8]"
                                  }`}
                                >
                                  {offer.type}
                                </div>
                              </div>
                              <div>
                                <div className="text-sm text-[#8C8A96]">
                                  Rate
                                </div>
                                <div className="text-sm text-[#34656D] font-medium">
                                  {offer.trader_offer[0]?.rate} ₹/kWh
                                </div>
                              </div>
                              <div>
                                <div className="text-sm text-[#8C8A96]">
                                  Quantum
                                </div>
                                <div className="text-sm text-[#34656D] font-medium">
                                  {offer.trader_offer[0]?.quantum} MW
                                </div>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </>
    );
  }

  // Full View (when slider is open)
  return (
    <div className="w-full h-full bg-white">
      {/* Header */}
      <div className="flex justify-between items-center pb-2 border-b border-[#DFEAF2] mb-2">
        <h1 className="text-lg font-medium text-[#34656d]">
          Procurement Console
        </h1>
        <button
          onClick={handleAddOffers}
          className="flex items-center px-4 py-2 gap-2 text-base rounded-xl bg-[#FFF] text-[#F1935C] font-medium border border-[#F1935C] hover:bg-[#FFF5ED] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus strokeWidth={3} className="w-4 h-4" /> Add Offers
        </button>
      </div>

      {/* Add Modal */}
      <AddOfferDataModal
        isModalOpen={isAddModalOpen}
        setIsModalOpen={setIsAddModalOpen}
        modalTitle="Add Offer"
        onSubmit={handleAddSubmit}
        loading={loading}
        setLoading={setLoading}
      />

      {/* Edit Modal */}
      {isEditModalOpen && editingOffer && (
        <EditOfferDataModal
          isModalOpen={isEditModalOpen}
          setIsModalOpen={setIsEditModalOpen}
          offerData={editingOffer}
          onUpdate={handleEditSubmit}
        />
      )}

      <div className="w-full !overflow-auto !h-[calc(100vh-350px)] custom-scroll">
        {/* MCP Section */}
        <div className="border border-[#CAD5E2] rounded-xl p-4 bg-[#FFF] mb-4 w-[100%] min-w-[430px]">
          <h3 className="text-base text-[#34656d] font-medium mb-2 pb-1 border-b border-[#E8EAED]">
            Market Scenario
          </h3>
          <div className="flex items-center justify-between gap-x-4 gap-y-3 mb-3">
            <div>
              <label className="block text-base text-[#9AA0A6] mb-0.5">
                Start Date
              </label>
              <div className="text-base text-[#34656D] font-medium">
                {SelectedStartDate}
              </div>
            </div>
            <div>
              <label className="block text-base text-[#9AA0A6] mb-0.5">
                End Date
              </label>
              <div className="text-base text-[#34656D] font-medium">
                {SelectedEndDate}
              </div>
            </div>
          </div>
          {/* Scenario Select */}
          <div className="relative">
            <select
              value={scenario}
              onChange={(e) => handleScenarioChange(e.target.value)}
              className="w-full py-1 px-4 pr-10 border border-[#A9A8B1] rounded-lg text-xl text-[#6F6D7C] bg-white appearance-none outline-none cursor-pointer"
            >
              <option value="">Select Scenario</option>
              <option value="1">Last Year</option>
              <option value="2">Last 2 Year Avg</option>
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-[0px] top-[-2px] flex items-center pr-[10px] text-[#9AA0A6]">
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>
          </div>
        </div>

        {/* Offers List */}
        <div className="space-y-6">
          <h3 className="text-base text-[#34656d] font-medium mb-4 pb-1 border-b border-[#E8EAED]">
            Current Offers
          </h3>
          {loading && offers.length === 0 ? (
            <div className="h-full w-full bg-white flex items-center justify-center">
              <img
                src={loader}
                width={200}
                alt="loader"
                className="block mt-[5rem]"
              />
            </div>
          ) : error ? (
            <div className="text-center py-8 text-red-500">{error}</div>
          ) : offers.length === 0 ? (
            <div className="text-gray-600 text-center py-12 border-2 border-dashed border-gray-200 rounded-lg">
              <h2 className="mb-2 text-lg text-gray-600">
                No offers added yet
              </h2>
              <p className="text-base text-gray-400">
                Click "Add Offers" to get started
              </p>
            </div>
          ) : (
            offers.map((offer) => (
              <div
                key={offer.id}
                className="w-[100%] min-w-[430px] border border-[#CAD5E2] rounded-xl p-4 bg-[#FFF] mb-2 relative"
              >
                {/* Offer Header */}
                <div className="flex items-center justify-between mb-5 pb-1 border-b border-[#E8EAED]">
                  <div className="flex items-center gap-1.5">
                    <div className="text-base text-[#9AA0A6] absolute top-[-15px] left-0 w-[100%]">
                      <span className="text-sm font-medium text-[#34656D] bg-[#F3F5F8] px-2 py-0 ml-2 rounded-full">
                        Offer Name
                      </span>
                    </div>
                    <span
                      className={`text-base font-semibold ${
                        offer.type === "RTC"
                          ? "text-[#F1935C]"
                          : offer.type === "PEAK"
                          ? "text-[#5B8A9F]"
                          : "text-[#718EBF]"
                      }`}
                    >
                      {offer.type}
                    </span>
                    <button
                      onClick={() => handleEditClick(offer)}
                      className="ml-1 hover:opacity-70 transition-opacity"
                    >
                      <Edit
                        strokeWidth={2.5}
                        size={18}
                        className="text-gray-500 hover:text-orange-500"
                      />
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleToggleOffer(offer.id)}
                    className={`relative inline-flex items-center h-5 w-10 rounded-full transition-colors focus:outline-none ${
                      offer.status ? "bg-[#F1935C]" : "bg-[#DADCE0]"
                    }`}
                  >
                    <span
                      className={`inline-block w-4 h-4 transform bg-white rounded-full shadow-sm transition-transform ${
                        offer.status ? "translate-x-5" : "translate-x-0.5"
                      }`}
                    />
                  </button>
                </div>

                {/* Offer Details */}
                <div className="pl-0">
                  <div className="flex items-center justify-around gap-x-4 gap-y-3 mb-3">
                    <div>
                      <label className="block text-base text-[#9AA0A6] mb-0.5">
                        Start Date
                      </label>
                      <div className="text-base text-[#34656D] font-medium">
                        {offer.start_date || ""}
                      </div>
                    </div>
                    <div>
                      <label className="block text-base text-[#9AA0A6] mb-0.5">
                        End Date
                      </label>
                      <div className="text-base text-[#34656D] font-medium">
                        {offer.end_date || ""}
                      </div>
                    </div>
                  </div>

                  {/* Trader Offers Section */}
                  <div className="space-y-3">
                    {offer.trader_offer.map((traderOffer, index) => (
                      <div key={index}>
                        {/* Slot Label for PEAK and CUSTOM */}
                        {(offer.type === "PEAK" || offer.type === "CUSTOM") && (
                          <div className="text-sm font-medium text-[#F1935C] mb-1 text-pretty">
                            {getSlotLabel(index, offer.type)}
                          </div>
                        )}

                        <div className="flex items-center justify-around gap-x-4 gap-y-3">
                          <div>
                            <label className="block text-sm text-[#9AA0A6] mb-0.5">
                              Start Time
                            </label>
                            <div className="text-base font-medium text-[#34656D]">
                              {traderOffer.start_slot || ""}
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm text-[#9AA0A6] mb-0.5">
                              End Time
                            </label>
                            <div className="text-base font-medium text-[#34656D]">
                              {traderOffer.end_slot || ""}
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm text-[#9AA0A6] mb-0.5">
                              Rate
                            </label>
                            <div className="text-base font-medium text-[#34656D]">
                              {traderOffer.rate
                                ? `${traderOffer.rate} ₹/kWh`
                                : ""}
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm text-[#9AA0A6] mb-0.5">
                              Quantum
                            </label>
                            <div className="text-base font-medium text-[#34656D]">
                              {traderOffer.quantum
                                ? `${traderOffer.quantum} MW`
                                : ""}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-center gap-2 pt-4">
        <button
          onClick={handleReset}
          className="w-[171px] h-[42px] text-base font-medium text-[#6F6D7C] bg-transparent border-none rounded-lg cursor-pointer hover:text-gray-700 transition-colors"
        >
          Reset
        </button>
        <button
          onClick={handleApply}
          disabled={applyLoading}
          className="w-[171px] h-[42px] text-base font-medium text-white bg-[#F1935C] rounded-lg cursor-pointer shadow-md shadow-orange-500/20 hover:bg-[#cd733f] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {applyLoading ? "Loading..." : "Apply"}
        </button>
      </div>
    </div>
  );
}

// Main PPEConsoleTable component
export default function PPEConsoleTable({
  open,
  setOpen,
  setError,
  refreshKey,
  setRefreshKey,
  activeTab,
}: {
  open: boolean;
  setOpen: any;
  setError?: any;
  refreshKey: any;
  setRefreshKey: any;
  activeTab: string;
}) {
  const [loading, setLoading] = useState(false);
  const [fullbtn, setFullBtn] = useState(false);

  const handleApply = async (ppeData: PPEFormData) => {
    setLoading(true);
    try {
      const payload = {
        mcpScenario: ppeData.scenario,
        offers: ppeData.offers.map((offer) => ({
          id: offer.id,
          type: offer.type,
          status: offer.status ? true : false,
        })),
      };
      const res = await UpdateTraderRuvnlOffersToggleData(payload);
      if (res) {
        setRefreshKey((prev: number) => prev + 1);
        setOpen(!open);
      }
    } catch (error) {
      console.error("Error submitting data", error);
      if (setError) setError("Error submitting data");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {};

  return (
    <div
      className="gas-chart-container simulation_table shadow-[rgba(149,157,165,0.2)_0px_8px_24px] bg-[#FFFFFF8C] overflow-auto"
      id="slide"
      style={{
        width: open ? "18%" : "27%",
        position: "relative",
        zIndex: "2",
        height: "calc(-195px + 100vh)",
        transition: "all 0.3s ease-out",
      }}
    >
      <button
        style={{
          width: fullbtn ? "100px" : "30px",
          overflow: "hidden",
          height: "30px",
          right: "0px",
        }}
        onMouseOver={() => setFullBtn(true)}
        onMouseLeave={() => setFullBtn(false)}
        onClick={() => setOpen(!open)}
        className="hide-btn flex items-center justify-between gap-1 transition-all text-nowrap duration-700 ease-in-out"
      >
        <ChevronLeft
          size={18}
          className={`text-primary transition-transform duration-300 ${
            !open ? "rotate-359" : "rotate-180"
          }`}
        />
        {fullbtn && (
          <>{open ? <span>Show Filters</span> : <span>Hide Filters</span>}</>
        )}
      </button>
      <PPEConsole
        loading={loading}
        onApply={handleApply}
        onReset={handleReset}
        refreshKey={refreshKey}
        setRefreshKey={setRefreshKey}
        isSliderOpen={!open}
        activeTab={activeTab}
      />
    </div>
  );
}
